import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError, NetworkError } from "../api/client";
import {
  approveMfaRecovery,
  listPendingMfaRecoveryRequests,
  readMfaRecoveryRequest,
  type MfaRecoveryReasonCode,
  type MfaRecoveryPendingItem,
  type MfaRecoveryRequestSummary,
} from "../api/mfaRecovery";
import { friendlyErrorMessage } from "../api/errors";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

const REQUEST_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const APPROVAL_RETURN = "/mfa/recuperacao/aprovar";

type ViewState =
  | "loading"
  | "pending"
  | "approving"
  | "success"
  | "expired"
  | "conflict"
  | "mfa_elevation_required"
  | "unauthorized"
  | "error";

const REASON_LABELS: Record<MfaRecoveryReasonCode, string> = {
  device_lost: "Dispositivo perdido",
  device_stolen: "Dispositivo furtado",
  device_damaged: "Dispositivo danificado",
  authenticator_unavailable: "Aplicativo autenticador indisponível",
};

function isRequestId(value: string | null): value is string {
  return Boolean(value && REQUEST_ID.test(value.trim()));
}

function isExpired(summary: MfaRecoveryRequestSummary): boolean {
  return summary.status === "recovery_expired" || (
    summary.status === "recovery_requested" && new Date(summary.request_expires_at).getTime() <= Date.now()
  );
}

const API_ERROR_MESSAGES: Record<string, string> = {
  "403:mfa_elevation_required": "Confirme seu autenticador para aprovar esta solicitação.",
  "403:forbidden": "Você não está autorizado a aprovar esta solicitação.",
  "409:recovery_expired": "A solicitação expirou antes da aprovação.",
  "409:recovery_conflict": "A solicitação mudou de estado e não pode ser aprovada novamente.",
};

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    return API_ERROR_MESSAGES[`${cause.status}:${cause.code}`] ?? "Não foi possível processar a aprovação agora. Tente novamente.";
  }
  if (cause instanceof NetworkError) return friendlyErrorMessage(cause);
  return "Não foi possível preparar a aprovação agora. Tente novamente.";
}

function errorView(cause: unknown): ViewState {
  if (!(cause instanceof ApiError)) return "error";
  if (cause.status === 403 && cause.code === "mfa_elevation_required") return "mfa_elevation_required";
  if (cause.status === 403) return "unauthorized";
  if (cause.status === 409 && cause.code === "recovery_expired") return "expired";
  if (cause.status === 409) return "conflict";
  return "error";
}

function formatDate(value: string | null): string {
  if (!value) return "Não definido";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Não definido";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function ApprovalSummary({ summary }: { summary: MfaRecoveryRequestSummary }) {
  return (
    <dl className="mfa-recovery-approval__summary">
      <div><dt>Titular</dt><dd>{summary.target_display_name}</dd></div>
      <div><dt>Motivo</dt><dd>{REASON_LABELS[summary.reason_code]}</dd></div>
      <div><dt>Referência</dt><dd>{summary.decision_reference}</dd></div>
      <div><dt>Solicitado em</dt><dd>{formatDate(summary.requested_at)}</dd></div>
      <div><dt>Prazo para análise</dt><dd>{formatDate(summary.request_expires_at)}</dd></div>
    </dl>
  );
}

interface LoadSummaryOptions {
  requestId: string;
  accessToken: string;
  aalCurrentLevel: string | null;
  aalStatus: string | null;
  expireSession: () => void;
  goToChallenge: () => void;
  setView: (view: ViewState) => void;
  setSummary: (summary: MfaRecoveryRequestSummary | null) => void;
  setError: (error: string | null) => void;
  setConfirmed: (confirmed: boolean) => void;
}

async function loadApprovalSummary(options: LoadSummaryOptions): Promise<void> {
  const { requestId, accessToken, aalCurrentLevel, aalStatus, expireSession, goToChallenge, setView, setSummary, setError, setConfirmed } = options;
  if (aalStatus === "loading") {
    setView("loading");
    return;
  }
  if (aalStatus === "unavailable") {
    setError("Não foi possível validar sua sessão elevada. Tente novamente.");
    setView("error");
    return;
  }
  setView("loading");
  setError(null);
  try {
    const result = await readMfaRecoveryRequest(accessToken, requestId);
    setSummary(result);
    if (isExpired(result)) {
      setView("expired");
      return;
    }
    if (result.status !== "recovery_requested") {
      setView("conflict");
      return;
    }
    setConfirmed(false);
    if (aalCurrentLevel !== "aal2") {
      goToChallenge();
      return;
    }
    setView("pending");
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 401) {
      expireSession();
      return;
    }
    setError(errorMessage(cause));
    setView(errorView(cause));
  }
}

function TerminalPanel({ view, error, onRetry }: { view: ViewState; error: string | null; onRetry: () => void }) {
  if (view === "success") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-success-title">
        <StatusBanner tone="success">Aprovação registrada com sucesso.</StatusBanner>
        <h2 id="mfa-recovery-approval-success-title">Solicitação autorizada</h2>
        <p className="form-card__note">O titular poderá concluir a recuperação dentro do prazo da autorização. Nenhum fator foi matriculado nesta etapa.</p>
      </section>
    );
  }
  if (view === "expired") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-expired-title">
        <StatusBanner tone="warning">A solicitação expirou antes da aprovação.</StatusBanner>
        <h2 id="mfa-recovery-approval-expired-title">Solicitação expirada</h2>
        <p className="form-card__note">Peça à Secretaria/DAF que registre uma nova solicitação presencial.</p>
      </section>
    );
  }
  if (view === "conflict") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-conflict-title">
        <StatusBanner tone="warning">A solicitação não está mais disponível.</StatusBanner>
        <h2 id="mfa-recovery-approval-conflict-title">Solicitação já processada</h2>
        <p className="form-card__note">Outro responsável pode ter concluído esta etapa. Não tente reapresentar o protocolo.</p>
      </section>
    );
  }
  if (view === "unauthorized") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-unauthorized-title">
        <StatusBanner tone="error">Acesso não autorizado.</StatusBanner>
        <h2 id="mfa-recovery-approval-unauthorized-title">Aprovação restrita</h2>
        <p className="form-card__note">Somente um responsável elegível pode analisar esta solicitação.</p>
      </section>
    );
  }
  if (view === "mfa_elevation_required") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-elevation-title">
        <StatusBanner tone="warning">Confirme seu autenticador para continuar.</StatusBanner>
        <h2 id="mfa-recovery-approval-elevation-title">Confirmação adicional necessária</h2>
        <p className="form-card__note">A aprovação exige AAL2/TOTP recente da sua própria sessão.</p>
        <Button type="button" onClick={onRetry}>Confirmar autenticador</Button>
      </section>
    );
  }
  if (view === "error") {
    return (
      <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-approval-error-title">
        <StatusBanner tone="error">{error ?? "Não foi possível carregar a aprovação."}</StatusBanner>
        <h2 id="mfa-recovery-approval-error-title">Tente novamente</h2>
        <Button type="button" variant="secondary" onClick={onRetry}>Tentar novamente</Button>
      </section>
    );
  }
  return null;
}

interface ApprovalContentProps {
  view: ViewState;
  summary: MfaRecoveryRequestSummary | null;
  error: string | null;
  confirmed: boolean;
  headingRef: RefObject<HTMLHeadingElement>;
  onConfirmChange: (confirmed: boolean) => void;
  onApprove: () => void;
  onCancel: () => void;
  onRetry: () => void;
}

function ApprovalContent({ view, summary, error, confirmed, headingRef, onConfirmChange, onApprove, onCancel, onRetry }: ApprovalContentProps) {
  const terminalState = ["success", "expired", "conflict", "mfa_elevation_required", "unauthorized", "error"].includes(view);
  return (
    <>
      <p className="mfa-challenge__eyebrow">Recuperação excepcional</p>
      <h1 id="mfa-recovery-approval-title" ref={headingRef} tabIndex={-1}>Aprovação independente</h1>
      <p className="form-card__intro">Analise o pedido presencial e confirme somente se você é o responsável autorizado e conhece o atendimento realizado.</p>
      {view === "loading" && <p role="status">Carregando o resumo da solicitação…</p>}
      {view === "approving" && <p role="status">Registrando a aprovação…</p>}
      {error && !terminalState && <StatusBanner tone="error">{error}</StatusBanner>}
      {summary && (view === "pending" || view === "approving") && (
        <>
          <ApprovalSummary summary={summary} />
          <div className="mfa-recovery-approval__confirmation">
            <label>
              <input type="checkbox" checked={confirmed} onChange={(event) => onConfirmChange(event.target.checked)} disabled={view === "approving"} />
              Confirmo que analisei este atendimento presencial e autorizo a recuperação excepcional.
            </label>
          </div>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={view === "approving"}>Cancelar</Button>
            <Button type="button" onClick={onApprove} loading={view === "approving"} disabled={!confirmed}>Aprovar recuperação</Button>
          </div>
        </>
      )}
      <TerminalPanel view={view} error={error} onRetry={onRetry} />
    </>
  );
}

export function MfaRecoveryApprovalPage() {
  const [params] = useSearchParams();
  const requestId = params.get("request_id")?.trim() ?? "";
  return requestId ? <MfaRecoveryApprovalDetailPage /> : <MfaRecoveryApprovalInbox />;
}

function MfaRecoveryApprovalInbox() {
  const { state, expireSession } = useAuth();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [items, setItems] = useState<MfaRecoveryPendingItem[]>([]);
  const [view, setView] = useState<"loading" | "ready" | "error">("loading");

  const load = useCallback(async () => {
    if (state.status !== "authenticated") return;
    setView("loading");
    try {
      const result = await listPendingMfaRecoveryRequests(state.accessToken);
      setItems(result);
      setView("ready");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      setView("error");
    }
  }, [expireSession, state]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (view !== "loading") headingRef.current?.focus(); }, [view]);

  if (state.status !== "authenticated") return null;

  return (
    <main className="centered-page">
      <section className="form-card mfa-recovery-approval-card" aria-labelledby="mfa-recovery-inbox-title">
        <p className="mfa-challenge__eyebrow">Recuperação excepcional</p>
        <h1 id="mfa-recovery-inbox-title" ref={headingRef} tabIndex={-1}>Aprovações MFA</h1>
        <p className="form-card__intro">Analise solicitações presenciais pendentes que exigem aprovação independente.</p>
        {view === "loading" && <p role="status">Carregando solicitações pendentes…</p>}
        {view === "error" && (
          <section className="mfa-recovery-approval__terminal" aria-labelledby="mfa-recovery-inbox-error-title">
            <StatusBanner tone="error">Não foi possível carregar as aprovações agora.</StatusBanner>
            <h2 id="mfa-recovery-inbox-error-title">Tente novamente</h2>
            <Button type="button" variant="secondary" onClick={() => void load()}>Tentar novamente</Button>
          </section>
        )}
        {view === "ready" && items.length === 0 && (
          <StatusBanner tone="info">Nenhuma solicitação de recuperação pendente no momento.</StatusBanner>
        )}
        {view === "ready" && items.length > 0 && (
          <ul className="mfa-recovery-approval__inbox" aria-label="Solicitações pendentes">
            {items.map((item) => (
              <li key={item.request_id} className="mfa-recovery-approval__inbox-item">
                <div>
                  <h2>{item.target_display_name}</h2>
                  <p>{item.target_username ? `@${item.target_username}` : "Usuário não identificado"} · {item.target_category === "efetivo" ? "Efetivo" : "Colaborador"}</p>
                  <p>{REASON_LABELS[item.reason_code]} · Referência {item.decision_reference}</p>
                  <p>Solicitado por {item.requested_by_username ? `@${item.requested_by_username}` : "usuário não identificado"} em {formatDate(item.created_at)}</p>
                  <p>Validade até {formatDate(item.request_expires_at)}</p>
                </div>
                <Button type="button" onClick={() => navigate(`/mfa/recuperacao/aprovar?request_id=${encodeURIComponent(item.request_id)}`)}>
                  Revisar solicitação
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function MfaRecoveryApprovalDetailPage() {
  const { state, expireSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [view, setView] = useState<ViewState>("loading");
  const [summary, setSummary] = useState<MfaRecoveryRequestSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const requestId = params.get("request_id")?.trim() ?? "";
  const accessToken = state.status === "authenticated" ? state.accessToken : null;
  const aalCurrentLevel = state.status === "authenticated" ? state.aal.currentLevel : null;
  const aalStatus = state.status === "authenticated" ? state.aal.status : null;

  const goToChallenge = useCallback(() => {
    navigate(`/mfa/desafio?retorno=${encodeURIComponent(APPROVAL_RETURN)}&request_id=${encodeURIComponent(requestId)}`, { replace: true });
  }, [navigate, requestId]);

  const loadSummary = useCallback(async () => {
    if (isRequestId(requestId) && state.status === "authenticated" && accessToken) {
      await loadApprovalSummary({ requestId, accessToken, aalCurrentLevel, aalStatus, expireSession, goToChallenge, setView, setSummary, setError, setConfirmed });
    }
  }, [aalCurrentLevel, aalStatus, accessToken, expireSession, goToChallenge, requestId, state.status]);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    if (!isRequestId(requestId)) {
      setError("O protocolo de aprovação é inválido ou está incompleto.");
      setView("error");
      return;
    }
    void loadSummary();
  }, [loadSummary, requestId, state.status]);

  useEffect(() => {
    if (view !== "loading") headingRef.current?.focus();
  }, [view]);

  async function approve() {
    if (view !== "pending" || !confirmed || !summary || state.status !== "authenticated") return;
    setView("approving");
    setError(null);
    try {
      await approveMfaRecovery(state.accessToken, requestId);
      setView("success");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        setView("unauthorized");
        return;
      }
      setError(errorMessage(cause));
      setView(errorView(cause));
    }
  }

  if (state.status !== "authenticated") return null;

  const retry = () => {
    if (view === "mfa_elevation_required") goToChallenge();
    else void loadSummary();
  };

  return (
    <main className="centered-page">
      <section className="form-card mfa-recovery-approval-card" aria-labelledby="mfa-recovery-approval-title">
        <Button type="button" variant="secondary" onClick={() => navigate(APPROVAL_RETURN)}>
          ← Voltar para lista de aprovações
        </Button>
        <ApprovalContent
          view={view}
          summary={summary}
          error={error}
          confirmed={confirmed}
          headingRef={headingRef}
          onConfirmChange={setConfirmed}
          onApprove={() => void approve()}
          onCancel={() => navigate("/dashboard")}
          onRetry={retry}
        />
      </section>
    </main>
  );
}
